import React, { useState, useEffect } from "react";
import "../../members/screens/MemberPrint.css";
import { BASE_URL } from "../../../env";
import {
  getDlRepaymentTerms,
  getDlScheduleDates,
  getDlPaymentForDay,
} from "../../../utils/dlCalculations";

// Daily Loan's repayment table, alongside BL/GPL's GeneratedEmiSchedule.js and
// JLG's JlgGenerateEmiSchedule.js. It cannot reuse either: DL is a flat annual
// rate pro-rated over the days actually held (not a monthly reducing-balance
// EMI), and it lists every one of the ~100-125 daily collections rather than
// 12-24 monthly ones - see .claude/skills/daily-loan/SKILL.md and
// utils/dlCalculations.js, whose exported functions this composes directly
// rather than re-deriving the math here.
const DlGenerateEmiSchedule = ({ memberDetails }) => {
  const [schedule, setSchedule] = useState([]);
  const [terms, setTerms] = useState(null);

  useEffect(() => {
    handleGenerateSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateSchedule = () => {
    const loanAmount =
      memberDetails.memberDetails.sanctionedLoanAmountBySanctionCommittee ||
      memberDetails.proposedLoanDetails.loanAmount ||
      0;
    const rateOfInterest =
      parseFloat(memberDetails.proposedLoanDetails.rateOfInterest) || 0;
    // tenureInMonths holds the day count for DL, not a month count.
    const tenureInDays =
      parseInt(memberDetails.proposedLoanDetails.tenureInMonths, 10) || 0;

    const computedTerms = getDlRepaymentTerms(
      loanAmount,
      rateOfInterest,
      tenureInDays
    );
    setTerms(computedTerms);

    // installmentStartDate is set by the Branch Manager at disbursement-terms
    // time and may not exist yet if this document is viewed earlier (e.g.
    // right after Sanction Committee approval). Fall back to today so the
    // schedule still renders with placeholder dates rather than nothing.
    const startDate =
      memberDetails.memberDetails.installmentStartDate || new Date();

    const dates = getDlScheduleDates(startDate, tenureInDays);
    let runningBalance = computedTerms.totalRepayable;

    const rows = dates.map((date, index) => {
      const instalment = getDlPaymentForDay(index, computedTerms);
      runningBalance = Math.max(0, runningBalance - instalment);
      return {
        day: index + 1,
        date: date.toLocaleDateString("en-GB"),
        instalment,
        balance: runningBalance,
      };
    });

    setSchedule(rows);
  };

  return (
    <div>
      <div>
        <h1 className="section text-center">Repayment Schedule</h1>
        <div>
          <table
            className="table table-striped text-center table-bordered"
            style={{
              width: "100%",
              fontSize: "26px",
              borderCollapse: "collapse",
              border: "1.5px solid black",
            }}
          >
            <tbody>
              <tr className="section">
                <td
                  style={{
                    width: "30%",
                    border: "1.5px solid black",
                    padding: "2px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div style={{ width: "50%" }}>
                        <b>Customer Name</b>
                      </div>
                      <div style={{ width: "50%" }}>
                        {memberDetails.memberDetails.memberName}
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      <div style={{ width: "50%" }}>
                        <b>Branch Name</b>
                      </div>
                      <div style={{ width: "50%" }}>{memberDetails.branchName}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      <div style={{ width: "50%" }}>
                        <b>Rate of Interest</b>
                      </div>
                      <div style={{ width: "50%" }}>
                        {memberDetails.proposedLoanDetails.rateOfInterest}%
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ width: "20%", border: "1.5px solid black" }}>
                  <img
                    src={`${BASE_URL}/images/${memberDetails.memberDocuments?.memberPhoto}`}
                    alt=""
                    style={{ width: "150px", height: "150px" }}
                  />
                </td>
                <td style={{ width: "20%", border: "1.5px solid black" }}>
                  <img
                    src={`${BASE_URL}/images/${memberDetails.nomineeDocuments?.nomineePhoto}`}
                    alt=""
                    style={{ width: "150px", height: "150px" }}
                  />
                </td>
                <td
                  style={{
                    width: "30%",
                    border: "1.5px solid black",
                    padding: "2px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div style={{ width: "50%" }}>
                        <b>Customer ID</b>
                      </div>
                      <div style={{ width: "50%" }}>
                        {memberDetails.memberDetails.customerId}
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      <div style={{ width: "50%" }}>
                        <b>Loan Amount</b>
                      </div>
                      <div style={{ width: "50%" }}>
                        {memberDetails.memberDetails.sanctionedLoanAmountBySanctionCommittee ??
                          memberDetails.proposedLoanDetails.loanAmount}
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      <div style={{ width: "50%" }}>
                        <b>Tenure</b>
                      </div>
                      <div style={{ width: "50%" }}>
                        {memberDetails.proposedLoanDetails.tenureInMonths} days
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      <div style={{ width: "50%" }}>
                        <b>Total Interest</b>
                      </div>
                      <div style={{ width: "50%" }}>{terms?.totalInterest ?? 0}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="table-responsive">
            <table
              className="table table-striped text-center table-bordered"
              style={{
                fontSize: "26px",
                borderCollapse: "collapse",
                border: "1.5px solid black",
              }}
            >
              <thead className="primaryColor thead_sticky bg-primary">
                <tr className="section">
                  <th style={{ border: "1.5px solid black", padding: "0" }}>Day</th>
                  <th style={{ border: "1.5px solid black", padding: "0" }}>Collection Date</th>
                  <th style={{ border: "1.5px solid black", padding: "0" }}>Daily Instalment</th>
                  <th style={{ border: "1.5px solid black", padding: "0" }}>Outstanding Balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((item) => (
                  <tr key={item.day} className="section">
                    <td style={{ border: "1.5px solid black", padding: "0" }}>{item.day}</td>
                    <td style={{ border: "1.5px solid black", padding: "0" }}>{item.date}</td>
                    <td style={{ border: "1.5px solid black", padding: "0" }}>{item.instalment}</td>
                    <td style={{ border: "1.5px solid black", padding: "0" }}>{item.balance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DlGenerateEmiSchedule;
